import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
  eventId: string | null;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Auth Error Boundary caught an error:', error, errorInfo);
    
    // Capture error with Sentry
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'auth',
        component: 'AuthErrorBoundary',
      },
      extra: {
        errorInfo,
      },
    });
    
    this.setState({
      error,
      errorInfo,
      eventId
    });
  }

  handleReset = () => {
    // Clear error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
    
    // Reload the page to reset authentication
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isAuthError = this.state.error?.message?.toLowerCase().includes('auth') ||
                         this.state.error?.message?.toLowerCase().includes('session');

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              {isAuthError ? 'Authentication Error' : 'Something went wrong'}
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              {isAuthError 
                ? 'We encountered an issue with your authentication. Please try refreshing the page.'
                : 'An unexpected error occurred. Our team has been notified.'}
            </p>

            {process.env.NODE_ENV === 'development' && (
              <details className="mb-6 p-4 bg-gray-50 rounded-lg">
                <summary className="cursor-pointer text-sm text-gray-700 font-medium">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs text-gray-600 overflow-auto">
                  {this.state.error?.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <button
              onClick={this.handleReset}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Refresh Page</span>
            </button>

            <p className="text-center text-sm text-gray-500 mt-4">
              If the problem persists, please{' '}
              <a href="mailto:support@rulevia.com" className="text-purple-600 hover:underline">
                contact support
              </a>
              {this.state.eventId && (
                <span className="block mt-2 text-xs text-gray-400">
                  Error ID: {this.state.eventId}
                </span>
              )}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}